import { useEffect, useState } from "react";
import { apiUrl } from "../api";
import { useAuth } from "../useAuth";

export interface AcquirerBranding {
  id: number;
  code: string;
  displayName: string;
  displayNameAr?: string | null;
  logoBase64?: string | null;
  logoMimeType?: string | null;
}

export type BrandingMap = Record<string, AcquirerBranding>;

let cache: BrandingMap | null = null;
let inflight: Promise<BrandingMap> | null = null;
const subscribers = new Set<(m: BrandingMap) => void>();

const notify = (m: BrandingMap) => {
  subscribers.forEach((cb) => cb(m));
};

const load = (token: string): Promise<BrandingMap> => {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = fetch(`${apiUrl}/acquirer-brandings`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((r) => (r.ok ? r.json() : []))
    .then((list: AcquirerBranding[]) => {
      const map: BrandingMap = {};
      for (const b of list) {
        if (b.code) map[b.code.toUpperCase()] = b;
      }
      cache = map;
      inflight = null;
      notify(map);
      return map;
    })
    .catch(() => {
      inflight = null;
      cache = {};
      notify(cache);
      return cache;
    });
  return inflight;
};

export const useAcquirerBrandings = (): BrandingMap => {
  const { token } = useAuth();
  const [map, setMap] = useState<BrandingMap>(cache || {});

  useEffect(() => {
    if (!token) return;
    const sub = (m: BrandingMap) => setMap(m);
    subscribers.add(sub);
    load(token).then((m) => setMap(m));
    return () => {
      subscribers.delete(sub);
    };
  }, [token]);

  return map;
};

export const brandingLogoDataUrl = (b?: AcquirerBranding | null): string | null =>
  b && b.logoBase64 ? `data:${b.logoMimeType || "image/png"};base64,${b.logoBase64}` : null;
